function asyncGeneratorStep(n, t, e, r, o, a, c) { try { var i = n[a](c), u = i.value; } catch (n) { return void e(n); } i.done ? t(u) : Promise.resolve(u).then(r, o); }
function _asyncToGenerator(n) { return function () { var t = this, e = arguments; return new Promise(function (r, o) { var a = n.apply(t, e); function _next(n) { asyncGeneratorStep(a, r, o, _next, _throw, "next", n); } function _throw(n) { asyncGeneratorStep(a, r, o, _next, _throw, "throw", n); } _next(void 0); }); }; }
// ----------------------------------------------------------------------------
// Convermax collection/search grid: Loyalty Pricing
// ----------------------------------------------------------------------------
// Convermax owns the product grid on collection and search pages — it renders
// its own `.cm_product-item` cards client-side from its own index, so the
// theme's card--product.liquid (and its server-side loyalty pricing) never runs
// there. This module re-adds the signed-in loyalty price on those cards.
//
// Convermax does NOT expose the product vendor or tags in the card markup, which
// loyalty pricing needs. What every card DOES have is a link to the product, so
// we read the product handle from that link and fetch `/products/{handle}.js`
// (cached, deduped) for the vendor / tags / price. The percentage + rounding then
// come from the SAME helpers the PDP and theme cards use
// (scripts/helpers/loyalty-pricing), so the price always matches checkout.
//
// Convermax re-renders the grid on filter / sort / paginate and may recycle the
// same DOM nodes for different products, so nothing here can be one-shot: we
// observe the document and re-evaluate each card, keying our work to the handle
// currently in the card so a recycled node is re-priced and a wiped overlay is
// restored — while our own writes are ignored (no loop).
//
// Only signed-in shoppers with an eligible product see anything; everyone else
// keeps Convermax's untouched price, matching the theme card's `customer &&
// pct > 0` gate.
// ----------------------------------------------------------------------------

// -------------------------------------
// Dependencies
// -------------------------------------

import { formatMoney } from '@shopify/theme-currency';
import Debug from 'scripts/libs/debug';
import { isExcludedByTags, loyaltyFactorFor, loyaltyPriceCents } from 'scripts/helpers/loyalty-pricing';

// -------------------------------------
// Selectors / config
// -------------------------------------

var CARD = '.cm_product-item';
var PRICE_CONTAINER = '.cm_product-item__price-container';
var PRODUCT_LINK = 'a[href*="/products/"]';
var MARKER = 'cm-loyalty-price'; // our injected node; its presence = "applied"

// handle -> Promise<productJson>. Dedupes the fetch across every card that
// shows the same product and across every re-render of the grid.
var productCache = new Map();

// price container -> handle currently being fetched, so concurrent scans don't
// launch duplicate requests for the same card.
var inFlight = new WeakMap();

// -------------------------------------
// Helpers
// -------------------------------------

var moneyFormat = () => window.theme && window.theme.settingCurrencyFormat || '{{amount}}';
var fromLabel = () => window.theme && window.theme.strings && window.theme.strings.fromPrice || 'From';
var regularLabel = () => window.theme && window.theme.strings && window.theme.strings.regularPrice || 'Regular price';

/**
 * The product handle a card points at, or null. Convermax links to
 * `/products/{handle}` (sometimes under a `/collections/.../` prefix and with a
 * `?variant=` query); we take the segment after `/products/`.
 * @param {Element} card
 * @returns {String|null}
 */
function handleFromCard(card) {
  var link = card.querySelector(PRODUCT_LINK);
  if (!link) return null;
  var match = /\/products\/([^/?#]+)/.exec(link.getAttribute('href') || '');
  return match ? decodeURIComponent(match[1]) : null;
}

/**
 * Fetch (and cache) the storefront product JSON for a handle.
 * @param {String} handle
 * @returns {Promise<Object>}
 */
function fetchProduct(handle) {
  if (!productCache.has(handle)) {
    var _window$Shopify;
    var promise = fetch("".concat(((_window$Shopify = window.Shopify) === null || _window$Shopify === void 0 || (_window$Shopify = _window$Shopify.routes) === null || _window$Shopify === void 0 ? void 0 : _window$Shopify.root) || '/', "products/").concat(handle, ".js"), {
      headers: {
        Accept: 'application/json'
      }
    }).then(res => {
      if (!res.ok) throw new Error("HTTP ".concat(res.status));
      return res.json();
    }).catch(error => {
      // Don't cache failures — allow a later scan to retry.
      productCache.delete(handle);
      throw error;
    });
    productCache.set(handle, promise);
  }
  return productCache.get(handle);
}

/**
 * Build the loyalty price markup for a product, or null when it doesn't qualify
 * (excluded by tag, or no matching rule for this shopper). Mirrors the theme
 * card (card--product.liquid): the loyalty price is shown as the price, the
 * original is struck. `<s>` supplies the strikethrough; the card__price classes
 * carry the styling.
 * @param {Object} product  storefront /products/{handle}.js payload (cents)
 * @returns {String|null}
 */
function loyaltyMarkup(product) {
  if (!product || isExcludedByTags(product.tags)) return null;
  var pct = loyaltyFactorFor(product.vendor);
  if (pct <= 0) return null;
  var fmt = moneyFormat();
  var regular = "<span class=\"visually-hidden\">".concat(regularLabel(), "</span>");
  if (product.price_varies) {
    var _base = product.price_min;
    var _loyalty = loyaltyPriceCents(_base, pct);
    var from = fromLabel();
    return "<span class=\"card__price ".concat(MARKER, "\">") + regular + "<s class=\"card__price--compare\">".concat(from, " ").concat(formatMoney(_base, fmt), "</s>") + "<span class=\"card__price--original\">".concat(from, " ").concat(formatMoney(_loyalty, fmt), "</span>") + '</span>';
  }
  var base = product.price;
  // Strike the compare-at when already on sale, else the non-loyalty price.
  var strike = product.compare_at_price || base;
  var loyalty = loyaltyPriceCents(base, pct);
  return "<span class=\"card__price ".concat(MARKER, "\">") + regular + "<s class=\"card__price--compare\">".concat(formatMoney(strike, fmt), "</s>") + "<span class=\"card__price--original\">".concat(formatMoney(loyalty, fmt), "</span>") + '</span>';
}

/**
 * Fetch the card's product and, if eligible, replace its price with the loyalty
 * price. Bails if the card is recycled for a different product mid-fetch. Records
 * the handle it resolved so scan() can tell "already done for THIS product" from
 * "recycled — re-evaluate".
 * @param {Element} card
 * @param {Element} container  the card's price container
 */
function enrich(_x, _x2) {
  return _enrich.apply(this, arguments);
} // -------------------------------------
// Scan / observe
// -------------------------------------
/**
 * Re-evaluate every card in the grid. Skips a card only when it's already
 * resolved for the product it currently shows (loyalty applied and our node
 * still present, or determined ineligible) — so a recycled node or a Convermax
 * re-render that wiped our overlay is picked back up.
 */
function _enrich() {
  _enrich = _asyncToGenerator(function* (card, container) {
    var handle = handleFromCard(card);
    if (!handle) return;
    if (inFlight.get(container) === handle) return;
    inFlight.set(container, handle);
    try {
      var product = yield fetchProduct(handle);
      // Convermax may have recycled this node for another product by now.
      if (handleFromCard(card) !== handle) return;
      var markup = loyaltyMarkup(product);
      container.dataset.loyaltyHandle = handle;
      if (markup) {
        container.dataset.loyaltyState = 'applied';
        container.innerHTML = markup;
      } else {
        // Not eligible — leave Convermax's own price untouched.
        container.dataset.loyaltyState = 'skip';
      }
    } catch (error) {
      Debug.warn('[convermax-loyalty] could not price card', {
        handle,
        error
      });
    } finally {
      if (inFlight.get(container) === handle) inFlight.delete(container);
    }
  });
  return _enrich.apply(this, arguments);
}
function scan() {
  document.querySelectorAll(CARD).forEach(card => {
    var container = card.querySelector(PRICE_CONTAINER);
    if (!container) return;
    var handle = handleFromCard(card);
    if (!handle) return;
    if (container.dataset.loyaltyHandle === handle) {
      var state = container.dataset.loyaltyState;
      if (state === 'skip') return;
      if (state === 'applied' && container.querySelector(".".concat(MARKER))) return;
    }
    enrich(card, container);
  });
}
var scheduled = false;
function scheduleScan() {
  if (scheduled) return;
  scheduled = true;
  window.requestAnimationFrame(() => {
    scheduled = false;
    scan();
  });
}
function init() {
  // Loyalty pricing is signed-in only; logged-out shoppers see Convermax's
  // price exactly as before, so there's nothing to do (and no reason to fetch).
  if (!window.loyaltyPricing || !window.loyaltyPricing.loggedIn) return;
  scheduleScan();

  // Convermax re-renders asynchronously and repeatedly (filter/sort/paginate);
  // a debounced document observer catches every new/recycled card. Our own
  // innerHTML writes retrigger it, but scan() no-ops on already-applied cards,
  // so there's no loop.
  new MutationObserver(scheduleScan).observe(document.body, {
    childList: true,
    subtree: true
  });
}
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}